import { useTranslation } from 'react-i18next';
import type {
  CommunicationDeliveryAttemptStatus,
  CustomerCommunicationStatus,
} from '@optiflow/types';
import { Badge } from '@/components/ui/Badge';

/**
 * What each status looks like, and — more importantly — what it is allowed to claim.
 *
 * `PROVIDER_ACCEPTED` is `brand`, not `success`, on purpose. It means WhatsApp's server took the
 * message; it does not mean the customer saw it, and a green tick beside "accepted" is exactly how
 * a shop assistant ends up telling somebody their glasses notification was delivered when nothing
 * has proven that. The only `success` tone belongs to `CONFIRMED_SENT`, where a person said so.
 *
 * `SUPPRESSED` is `neutral` rather than `danger` for the same kind of reason: nothing went wrong.
 * OptiFlow decided not to send, and dressing a deliberate decision as a failure sends staff looking
 * for a fault that does not exist.
 */
const STATUS_TONE: Record<
  CustomerCommunicationStatus,
  'neutral' | 'success' | 'warning' | 'danger' | 'brand'
> = {
  PREPARED: 'neutral',
  QUEUED: 'neutral',
  SENDING: 'warning',
  PROVIDER_ACCEPTED: 'brand',
  HANDED_OFF: 'warning',
  CONFIRMED_SENT: 'success',
  FAILED: 'danger',
  CANCELLED: 'neutral',
  SUPPRESSED: 'neutral',
};

const ATTEMPT_TONE: Record<
  CommunicationDeliveryAttemptStatus,
  'neutral' | 'success' | 'warning' | 'danger' | 'brand'
> = {
  QUEUED: 'neutral',
  SENDING: 'warning',
  PROVIDER_ACCEPTED: 'brand',
  FAILED: 'danger',
  CANCELLED: 'neutral',
  OUTCOME_AMBIGUOUS: 'warning',
};

export function CommunicationStatusBadge({ status }: { status: CustomerCommunicationStatus }) {
  const { t } = useTranslation();
  return <Badge tone={STATUS_TONE[status]}>{t(`crm.status.${status}`)}</Badge>;
}

export function AttemptStatusBadge({ status }: { status: CommunicationDeliveryAttemptStatus }) {
  const { t } = useTranslation();
  return <Badge tone={ATTEMPT_TONE[status]}>{t(`crm.attemptStatus.${status}`)}</Badge>;
}
